import { Button } from '@/components/ui/shadcn/button';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { useTranslations } from '@/hooks/useTranslations';
import { router } from '@inertiajs/react';
import { Pencil, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { AddSettingItemDialog } from './AddSettingItemDialog';
import { ManageSettingItemsDialog } from './ManageSettingItemsDialog';

interface MultiSelectSettingDropdownProps {
    settingKey: string;
    selected: string[];
    onChange: (selected: string[]) => void;
    setting: {
        value: unknown;
        translation_group?: string | null;
        editable_mode?: string;
        item_translations?: { en: Record<string, string>; lt: Record<string, string> } | null;
    };
    placeholder?: string;
    className?: string;
    disabled?: boolean;
}

export function MultiSelectSettingDropdown({
    settingKey,
    selected,
    onChange,
    setting,
    placeholder = 'Select options...',
    className,
    disabled = false,
}: MultiSelectSettingDropdownProps) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const { t } = useTranslations();
    const translationGroup = setting.translation_group;

    // Helper to translate a value
    const translateValue = (val: string): string => {
        if (!translationGroup) {
            return val;
        }
        const translationKey = `settings.${translationGroup}.${val}`;
        const translated = t(translationKey, val);
        return translated === translationKey ? val : translated;
    };

    // Parse setting value to get options
    const options = useMemo(() => {
        const settingValue = setting.value;

        if (!settingValue) {
            return [];
        }

        // Handle array of strings
        if (Array.isArray(settingValue) && settingValue.length > 0 && typeof settingValue[0] === 'string') {
            return settingValue.map((val: string) => ({
                value: val,
                label: translateValue(val),
            }));
        }

        // Handle array of objects with value/label
        if (Array.isArray(settingValue) && settingValue.length > 0 && typeof settingValue[0] === 'object') {
            return settingValue.map((item: { value?: string; label?: string } | string) => {
                const itemValue = typeof item === 'string' ? item : item.value || '';
                const itemLabel = typeof item === 'string' ? undefined : item.label;
                return {
                    value: itemValue,
                    label: itemLabel || translateValue(itemValue),
                };
            });
        }

        // Handle object with value/label pairs
        if (typeof settingValue === 'object' && !Array.isArray(settingValue)) {
            return Object.entries(settingValue as Record<string, unknown>).map(([key, val]: [string, unknown]) => {
                if (typeof val === 'object' && val !== null && 'label' in val) {
                    const obj = val as { value?: string; label?: string };
                    return {
                        value: obj.value || key,
                        label: obj.label || translateValue(obj.value || key),
                    };
                }
                return {
                    value: key,
                    label: typeof val === 'string' ? val : translateValue(key),
                };
            });
        }

        return [];
    }, [setting.value, translationGroup, t]);

    // Determine setting type for dialog
    const settingType = useMemo(() => {
        const settingValue = setting.value;

        if (Array.isArray(settingValue) && settingValue.length > 0) {
            if (typeof settingValue[0] === 'string') {
                return 'string';
            }
            if (typeof settingValue[0] === 'object' && 'value' in settingValue[0]) {
                return 'value_label';
            }
        }

        if (typeof settingValue === 'object' && !Array.isArray(settingValue)) {
            return 'value_label';
        }

        return 'string';
    }, [setting.value]);

    const handleAddSuccess = () => {
        // Reload settings + translations so newly added/edited labels show immediately
        router.reload({ only: ['settings', 'translations'] });
    };

    return (
        <div className="flex gap-2">
            <div className="flex-1">
                <MultiSelect
                    options={options}
                    selected={selected}
                    onChange={onChange}
                    placeholder={placeholder}
                    className={className}
                    disabled={disabled}
                    displayMode="count"
                />
            </div>
            {!disabled && (
                <>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setDialogOpen(true)}
                        title={t('settings.manage_items.add', 'Add new item')}
                    >
                        <Plus className="h-4 w-4" />
                    </Button>
                    {translationGroup && (
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setManageOpen(true)}
                            title={t('settings.manage_items.title', 'Manage items & translations')}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                    )}
                    <AddSettingItemDialog
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        settingKey={settingKey}
                        settingType={settingType}
                        onSuccess={handleAddSuccess}
                        translationGroup={setting.translation_group || null}
                    />
                    {translationGroup && (
                        <ManageSettingItemsDialog
                            open={manageOpen}
                            onOpenChange={setManageOpen}
                            settingKey={settingKey}
                            setting={setting}
                            onSuccess={handleAddSuccess}
                        />
                    )}
                </>
            )}
        </div>
    );
}
